import styles from "./Lessons.module.css";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../api";
import Card from "../components/cards/Card";
import { endOfDay, startOfDay } from "date-fns";
import Table from "../components/Table";
import { formatSecondsDuration, formatTimeV2 } from "../utils";
import { Each } from "../common/Each";
import useContextMenu from "../common/hooks/useContextMenu";
import AlertDialog from "../components/dialogs/AlertDialog";
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg";
import { ReactComponent as DownloadIcon } from "../assets/images/icons/ic-download.svg";
import { ReactComponent as RecordingIcon } from "../assets/images/icons/ic-video-lesson.svg";
import { ReactComponent as FilterIcon } from "../assets/images/icons/ic-filter.svg";
import MainContext from "../common/MainContext";
import { AdminRole } from "../common/constants";
import StatusBadge from "../components/StatusBadge";
import Dialog from "../components/dialogs/Dialog";
import LessonEditor from "../components/LessonEditor";
import { DialogStatus } from "../enums";
import TextInput from "../components/TextInput";
import EasePicker, { AmpPlugin, PresetPlugin, RangePlugin } from "react-easepick";
import Select from "../components/Select";
import Pagination from "../components/Pagination";
import useWindowDimensions from "../common/hooks/useWindowDimensions";

const getTodayEndIso = () => endOfDay(new Date()).toISOString().replace('Z', '');
const DEFAULT_START_AFTER = new Date(Date.UTC(2023, 0, 1, 0, 0, 0, 0)).toISOString().replace('Z', '');
const toUTCDate = (isoWithoutZ) => (isoWithoutZ ? new Date(`${isoWithoutZ}Z`) : null);

const Lessons = () => {

    const { width } = useWindowDimensions()
    const [lessons, setLessons] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [per] = useState(25);
    const [loading, setLoading] = useState(false);
    const [lesson, setLesson] = useState(null);
    const [lessonDelete, setLessonDelete] = useState(null)
    const [forceDeleteLesson, setForceDeleteLesson] = useState(null)
    const [filters, setFilters] = useState(() => ({
        q: '',
        startAfter: DEFAULT_START_AFTER,
        endBefore: getTodayEndIso(),
        module_id: null,
        edition_id: null,
        course_id: null,
        recording_status: null,
    }));
    const [filterOpen, setFilterOpen] = useState(false);

    const [courses, setCourses] = useState([])
    const [editions, setEditions] = useState([])
    const [loadingEditions, setLoadingEditions] = useState(false)
    const [modules, setModules] = useState([])
    const [showPagination, setShowPagination] = useState(true)

    const handleFilterChange = useCallback((updater) => {
        setFilters((prev) => updater(prev));
        setPage(0);
    }, []);

    const options = useMemo(
        () => ({
            css: [
                'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
                '/css/easepick-range.css',
            ],
            zIndex: 1000,
            plugins: [
                RangePlugin,
                PresetPlugin,
                AmpPlugin
            ],
            lang: 'it-IT',
            format: "DD/MM/YYYY",
            AmpPlugin: {
                resetButton: () => {
                    handleFilterChange((prev) => ({
                        ...prev,
                        startAfter: DEFAULT_START_AFTER,
                        endBefore: getTodayEndIso()
                    }));
                    return true
                },
                darkMode: false,
                dropdown: {
                    months: true,
                    years: true,
                    minYear: 2020
                }
            },
            PresetPlugin: {
                customPreset: {
                    'Oggi': [
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '7 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 6); // 6 giorni fa
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Settimana': [
                        (() => {
                            const now = new Date();
                            const day = now.getDay(); // 0 (domenica) - 6 (sabato)
                            const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Lunedì
                            const monday = new Date(now.setDate(diff));
                            return new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '30 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 29); // 29 giorni fa
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Mese': [
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0); // Primo giorno del mese
                        })(),
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999); // Ultimo giorno del mese
                        })(),
                    ],
                    'Anno': [
                        new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
                        new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999),
                    ]
                }
            },
            setup(picker) {
                picker.on("select", (e) => {
                    const { start, end } = e.detail;
                    handleFilterChange((prev) => ({
                        ...prev,
                        startAfter: start ? startOfDay(start).toISOString().replace('Z', '') : DEFAULT_START_AFTER,
                        endBefore: end ? endOfDay(end).toISOString().replace('Z', '') : getTodayEndIso(),
                    }));
                });
            },
        }),
        [handleFilterChange]
    );

    const recordingStatusOptions = useMemo(
        () => [
            { id: 'true', name: 'Con registrazione', value: 'true' },
            { id: 'false', name: 'Senza registrazione', value: 'false' },
        ],
        []
    );


    const getLessons = useCallback(async () => {
        setLoading(true);
        try {

            const params = new URLSearchParams();

            const activeFilters = filters

            if (activeFilters.startAfter) {
                params.append("start_after", activeFilters.startAfter);
            }
            if (activeFilters.endBefore) {
                params.append("end_before", activeFilters.endBefore);
            }
            if (activeFilters.course_id) {
                params.append("course_id", activeFilters.course_id)
            }
            if (activeFilters.edition_id) {
                params.append("edition_id", activeFilters.edition_id);
            }
            if (activeFilters.module_id) {
                params.append("module_id", activeFilters.module_id);
            }
            if (activeFilters.q) {
                params.append("q", activeFilters.q);
            }
            if (activeFilters.recording_status !== null) {
                params.append("recording_status", activeFilters.recording_status);
            }

            params.append("page", page);
            params.append("per", per);

            let l = await api.get(`/admin/lessons?${params.toString()}`);
            setLessons(l.page);
            setTotal(l.total);
        }
        catch (e) {
            console.error(e)
        }
        finally {
            setLoading(false);
        }
    }, [filters, page, per]);

    useEffect(() => {
        const getCourses = async () => {
            try {
                const c = await api.get(`/admin/courses`)
                setCourses(c.sort((a, b) => a.name.localeCompare(b.name)))
            }
            catch (e) {
                console.error(e)
            }
        }

        getCourses()
    }, []);

    useEffect(() => {
        let cancelled = false;
        setEditions([]);
        setModules([]);
        if (!filters.course_id) {
            setLoadingEditions(false);
            return;
        }
        setLoadingEditions(true);
        api.get(`/admin/courses/${filters.course_id}/editions`)
            .then((items) => {
                if (!cancelled) setEditions(items);
            })
            .catch((error) => {
                if (!cancelled) console.error(error);
            })
            .finally(() => {
                if (!cancelled) setLoadingEditions(false);
            });
        return () => { cancelled = true; };
    }, [filters.course_id]);

    useEffect(() => {
        getLessons();
    }, [getLessons]);

    const getRecordingStatus = (lesson) => {
        if (lesson.to_process === false) {
            return { name: 'Non caricare', color: 'rgba(var(--text-color-rgb), 55%)' };
        }
        if (!lesson.video_url) {
            return { name: 'Da caricare', color: 'var(--sf-red)' };
        }
        else {
            return { name: 'Caricata', color: 'var(--sf-light-green)' };
        }
    }

    const deleteLesson = async (lesson_id, force = false) => {
        try {
            const res = await api.delete(`/admin/lessons/${lesson_id}?force=${force}`)
            if (res && res.need_force) {
                setForceDeleteLesson(lesson_id)
            }
        } catch (e) {
            console.error(e)
        }
    }

    return (
        <HeaderFooterLayout>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <header className={styles.header}>
                            <div>
                                <p className={styles.eyebrow}>Didattica</p>
                                <h1>Lezioni</h1>
                                <p>Gestisci le lezioni e consulta lo stato delle registrazioni.</p>
                            </div>
                        </header>
                        <Card appearance="modern" integratedTable title="Elenco lezioni" actions={<span className={styles.resultCount} role="status">{loading ? 'Caricamento…' : `${total} lezioni`}</span>} style={{ width: '100%', overflow: 'visible' }}>
                            <div className={styles.search}>
                                <TextInput
                                    appearance="modern"
                                    value={filters.q}
                                    onKeyUp={(value) => {
                                        handleFilterChange((prev) => ({
                                            ...prev,
                                            q: value
                                        }))
                                    }}
                                    type="search"
                                    placeholder={"Cerca una lezione..."} />
                                <button
                                    type="button"
                                    className={`${styles.filterButton} ${filterOpen ? styles.active : ''}`}
                                    aria-expanded={filterOpen}
                                    aria-controls="lesson-filters"
                                    onClick={() => setFilterOpen((open) => !open)}>
                                    <FilterIcon aria-hidden="true" />
                                    Filtri
                                </button>
                            </div>
                            <div id="lesson-filters" className={styles.filters} hidden={!filterOpen}>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>Periodo</span>
                                    <EasePicker
                                        options={options}
                                        placeholder="Seleziona Date"
                                        startDate={toUTCDate(filters.startAfter)}
                                        endDate={toUTCDate(filters.endBefore)}
                                        className={styles.datePicker} />
                                </div>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>Corso</span>
                                    <Select portal
                                        appearance="modern"
                                        options={courses.map(c => ({ id: c.id, name: c.name, value: c.id }))}
                                        selected={courses.map(c => ({ id: c.id, name: c.name, value: c.id })).find(c => c.id === filters.course_id)}
                                        placeholder="Tutti i corsi" nullable nullLabel="Tutti i corsi"
                                        onSelect={(option) => {
                                            if (!option || option.id === -1) {
                                                handleFilterChange((prev) => ({
                                                    ...prev,
                                                    course_id: null,
                                                    edition_id: null,
                                                    module_id: null
                                                }))
                                                setEditions([])
                                                setModules([])
                                                return
                                            }
                                            handleFilterChange((prev) => ({
                                                ...prev,
                                                course_id: option ? option.id : null,
                                                edition_id: null,
                                                module_id: null
                                            }))
                                            setEditions([])
                                            setModules([])
                                        }} />
                                </div>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>Edizione</span>
                                    <Select portal
                                        appearance="modern"
                                        placeholder={loadingEditions ? "Caricamento edizioni…" : "Tutte le edizioni"}
                                        disabled={!filters.course_id || loadingEditions}
                                        loading={loadingEditions}
                                        nullable
                                        nullLabel="Tutte le edizioni"
                                        options={editions.map((e) => ({ id: e.id, name: e.name, value: e.id }))}
                                        selected={editions.map((e) => ({ id: e.id, name: e.name, value: e.id })).find(e => e.id === filters.edition_id)}
                                        onSelect={(option) => {
                                            if (!option || option.id === -1) {
                                                handleFilterChange((prev) => ({
                                                    ...prev,
                                                    edition_id: null,
                                                    module_id: null
                                                }))
                                                setModules([])
                                                return
                                            }
                                            const selectedEditionId = option ? option.id : null
                                            const selectedEdition = editions.find(ed => ed.id === selectedEditionId)
                                            handleFilterChange((prev) => ({
                                                ...prev,
                                                edition_id: selectedEditionId,
                                                module_id: null
                                            }))
                                            setModules(selectedEdition?.modules ?? [])
                                        }}
                                    />
                                </div>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>Modulo</span>
                                    <Select portal
                                        appearance="modern"
                                        placeholder="Tutti i moduli"
                                        options={modules.map((m) => ({ id: m.id, name: m.name, value: m.id }))}
                                        selected={modules.map((m) => ({ id: m.id, name: m.name, value: m.id })).find(m => m.id === filters.module_id)}
                                        onSelect={(option) => {
                                            if (!option || option.id === -1) {
                                                handleFilterChange((prev) => ({
                                                    ...prev,
                                                    module_id: null
                                                }))
                                                return
                                            }
                                            handleFilterChange((prev) => ({
                                                ...prev,
                                                module_id: option ? option.id : null
                                            }))
                                        }}
                                        nullable
                                        nullLabel="Tutti i moduli"
                                    />
                                </div>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>Registrazione</span>
                                    <Select portal
                                        appearance="modern"
                                        placeholder="Stato registrazione"
                                        options={recordingStatusOptions}
                                        selected={recordingStatusOptions.find(option => option.id === filters.recording_status)}
                                        onSelect={(option) => {
                                            if (!option || option.id === -1) {
                                                handleFilterChange((prev) => ({
                                                    ...prev,
                                                    recording_status: null
                                                }))
                                                return
                                            }
                                            handleFilterChange((prev) => ({
                                                ...prev,
                                                recording_status: option.value
                                            }))
                                        }}
                                        nullable
                                        nullLabel="Tutti gli stati"
                                    />
                                </div>
                            </div>
                            <Table
                                appearance="minimal"
                                onRowClick={(lesson) => {
                                    setLesson(lesson)
                                }}
                                style={{ border: 0, boxShadow: 'none', borderRadius: 0, marginTop: 0 }}
                                loading={loading}
                                loadingN={25}
                                columns={[
                                    { key: 'id', label: 'ID', type: 'number' },
                                    { key: 'name', label: 'NOME', type: 'string', wrap: true },
                                    { key: 'room_name', label: 'STANZA', type: 'string' },
                                    { key: 'date', label: 'DATA', type: 'date' },
                                    { key: 'hour', label: 'ORA', type: 'string' },
                                    { key: 'recordings', label: 'REGISTRAZIONI', type: 'custom' },
                                    { key: 'recording_status', label: 'STATO REGISTRAZIONE', type: 'custom' },
                                    { key: 'course', label: 'CORSO', type: 'string', wrap: true },
                                    { key: 'edition', label: 'EDIZIONE', type: 'string', wrap: true },
                                    { key: 'module', label: 'MODULO', type: 'string', wrap: true },
                                ]}
                                elements={lessons.map(l => ({
                                    ...l,
                                    date: l.starts_at,
                                    hour: `${formatTimeV2(l.starts_at)} - ${formatTimeV2(l.ends_at)}`,
                                    recordings: { value: 0, type: 'number', component: <Recordings lesson={l} /> },
                                    recording_status: { value: getRecordingStatus(l).name, type: 'string', component: <div className={styles.recordingStatus}><StatusBadge appearance="soft" label={getRecordingStatus(l).name} color={getRecordingStatus(l).color} /></div> },
                                    course: l.course.name,
                                    edition: l.edition.name,
                                    module: l.module.name,
                                }))}
                            />
                            {
                                showPagination &&
                                <div className={styles.pagination}><Pagination group={width > 540 ? 5 : 0} total={total} per={per} page={page} onChange={p => {
                                    setPage(p)
                                }} loading={loading} /></div>
                            }
                        </Card>
                    </div>
                </div>
            </div>
            {lesson &&
                <Dialog
                    open={lesson}
                    appearance="modern"
                    title={lesson?.id ? 'Modifica lezione' : 'Nuova lezione'}
                    onClose={() => {
                        setLesson(null)
                    }}>
                    <LessonEditor onCancel={() => setLesson(null)} lesson={lesson} module_id={lesson.module.id} onDelete={() => { setLessonDelete(lesson.id) }} onSave={(res, { applyThumbnailToEdition = false } = {}) => {
                        setLessons((current) => current.map((row) => {
                            if (row.id === res.id) return { ...row, ...res }
                            if (applyThumbnailToEdition && lesson.edition?.id != null && row.edition?.id === lesson.edition.id) {
                                return { ...row, thumbnail: res.thumbnail }
                            }
                            return row
                        }))
                        setLesson(null)
                    }} />
                </Dialog>
            }

            {lessonDelete &&
                <AlertDialog
                    open={lessonDelete}
                    title={"Sei sicuro di voler eliminare la lezione?"}
                    text={"Questa operazione non può essere annullata."}
                    actions={[
                        {
                            label: "ANNULLA",
                            onClick: () => { setLessonDelete(null) }
                        },
                        {
                            label: "ELIMINA",
                            accentColor: "var(--sf-red)",
                            onClick: async () => {
                                await deleteLesson(lessonDelete, false)
                                setLessonDelete(null)
                            }
                        }
                    ]}
                    status={DialogStatus.Warning}
                    onClose={() => {
                        setLessonDelete(null)
                    }}
                />
            }
            {forceDeleteLesson &&
                <AlertDialog
                    open={forceDeleteLesson}
                    title={"Eliminare i dati storici?"}
                    text={"Sono presenti dati storici degli utenti che hanno partecipato alla lezione. Sei sicuro di voler eliminare la lezione?"}
                    actions={[
                        {
                            label: "ANNULLA",
                            onClick: () => { setForceDeleteLesson(null) }
                        },
                        {
                            label: "ELIMINA",
                            accentColor: "var(--sf-red)",
                            confirmText: "elimina",
                            onClick: async () => {
                                await deleteLesson(forceDeleteLesson, true)
                                setForceDeleteLesson(null)
                                setLessonDelete(null)
                                setLesson(null)
                            }
                        }
                    ]}
                    status={DialogStatus.Warning}
                    onClose={() => {
                        setForceDeleteLesson(null)
                    }}
                />
            }
        </HeaderFooterLayout>
    )

}

const Recordings = ({ lesson }) => {

    const context = useContext(MainContext);

    // Local state derived from lesson.recordings without mutating props
    const [recordings, setRecordings] = useState(() =>
        lesson.recordings.map((rec) =>
            rec.status === "in-progress"
                ? { ...rec, duration: Math.floor(Date.now() / 1000) - rec.start_ts }
                : { ...rec }
        )
    );
    const [recordingToDelete, setRecordingToDelete] = useState(null);
    const [recordingsExpanded, setRecordingsExpanded] = useState(false);

    const { showMenu } = useContextMenu();

    useEffect(() => {
        if (lesson) {
            setRecordings(
                lesson.recordings.map((rec) =>
                    rec.status === "in-progress"
                        ? { ...rec, duration: Math.floor(Date.now() / 1000) - rec.start_ts }
                        : { ...rec }
                )
            );
        }
    }, [lesson]);

    // Non-mutating sorted list for rendering
    const sortedRecordings = useMemo(
        () => [...recordings].sort((a, b) => (b.duration || 0) - (a.duration || 0)),
        [recordings]
    );

    // What to display based on expand/collapse and count
    const displayRecordings = useMemo(() => {
        if (sortedRecordings.length > 1 && !recordingsExpanded) {
            return sortedRecordings.slice(0, 1);
        }
        return sortedRecordings;
    }, [sortedRecordings, recordingsExpanded]);

    const downloadRecording = async (recording_id) => {
        try {
            const response = await api.get(`/admin/recordings/${recording_id}/download`);
            const link = document.createElement("a");
            link.href = response.download_link;
            link.setAttribute("download", `${lesson.room_name}.mp4`);
            document.body.appendChild(link);
            link.click();
        } catch (e) {
            console.error(e);
        }
    };

    const dialogActions = useMemo(() => {
        const deleteRecording = async (recording_id) => {
            try {
                await api.delete(`/admin/recordings/${recording_id}`);
                setRecordings((prev) => prev.filter((rec) => rec.id !== recording_id));
            } catch (e) {
                console.error(e);
            }
        };

        return [
            {
                label: "ELIMINA",
                accentColor: "var(--sf-red)",
                confirmText: "ELIMINA",
                onClick: async () => {
                    deleteRecording(recordingToDelete);
                    setRecordingToDelete(null);
                },
            },
        ];
    }, [recordingToDelete]);

    return (
        <>
            <div className={`${styles.recordings} ${recordingsExpanded ? styles.expanded : ""}`}>
                <Each
                    of={displayRecordings}
                    render={(recording) => {
                        return (
                            <div
                                className={`${styles.recording} ${recording.status === "in-progress" ? styles.inProgress : ""
                                    }`}
                                onContextMenu={(e) => {
                                    if (recording.status === "finished" && context.user.role === AdminRole.Main) {
                                        showMenu(e, [
                                            {
                                                name: "Scarica",
                                                icon: <DownloadIcon style={{ color: "var(--sf-green)" }} />,
                                                onClick: () => downloadRecording(recording.id),
                                            },
                                            {
                                                name: "Elimina",
                                                icon: <DeleteIcon style={{ color: "var(--sf-red)" }} />,
                                                onClick: () => setRecordingToDelete(recording.id),
                                            },
                                        ]);
                                    }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (recording.status === "finished") {
                                        downloadRecording(recording.id);
                                    }
                                }}
                            >
                                <RecordingIcon style={{ width: "14px", height: "14px" }} />
                                <div className={styles.recordingDuration}>
                                    {formatSecondsDuration(recording.duration)}
                                </div>
                            </div>
                        );
                    }}
                />
                {recordings.length > 1 && !recordingsExpanded && (
                    <div
                        className={`${styles.recording}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setRecordingsExpanded(true);
                        }}
                        title="Mostra tutte le registrazioni"
                    >
                        <div className={styles.recordingDuration}>+{recordings.length - 1}</div>
                    </div>
                )}
            </div>
            <AlertDialog
                open={recordingToDelete}
                title="Elimina registrazione"
                text={`Confermi di voler eliminare la registrazione per la lezione '${lesson.name}'? L'azione non può essere annullata e la registrazione non può essere recuperata.`}
                onClose={() => setRecordingToDelete(null)}
                actions={dialogActions}
            />
        </>
    )
}

export default Lessons;
